import {
  BUYING_CATEGORY,
  CH_BUYING_FEATURE,
  CH_HOME_FEATURE,
  CH_HOME_LINK,
  CH_RENTING_FEATURE,
  CH_SELLING_FEATURE,
  CH_STUDENTS_FEATURE,
  DEFAULT_META_DESCRIPTION,
  NEWS_CATEGORIES,
  NEWS_FEATURE,
  RENTING_CATEGORY,
  SELLING_CATEGORY,
  STUDENTS_CATEGORY,
} from '@propertypal/shared/src/constants/content-hub';
import { ContentHubArticleCategory } from '@propertypal/shared/src/types/strapi/overrides';
import { getArticleCategoryParams } from '@propertypal/shared/src/utils/content-hub';
import chArticleSSR, { ContentHubArticleProps } from '@propertypal/shared/src/utils/content-hub/chArticleSSR';
import ArticleContent from '@propertypal/web-ui/src/article/ArticleContent';
import RelatedArticles from '@propertypal/web-ui/src/article/RelatedArticles';
import ContentHubPageContainer from '@propertypal/web-ui/src/content-hub/ContentHubPageContainer';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import React from 'react';
import ContentHubLayout from '../../components/layout/ContentHubLayout';
import generateMetaTags from '../../components/layout/MetaTags';
import cachedRequest from '../../services/cachedRequest.strapi';

export const getServerSideProps: GetServerSideProps<ContentHubArticleProps> = async ({ query }) => {
  const { subCategory, article } = query;

  // Handle navigating directly to `/content-hub/article`
  if (!article)
    return {
      redirect: {
        permanent: false,
        destination: CH_HOME_FEATURE ? CH_HOME_LINK : '/',
      },
    };

  // get the category enum
  const category = query.category as ContentHubArticleCategory;
  const { params, articlesParams } = getArticleCategoryParams();

  const subCategories = await cachedRequest('/article-sub-categories', 6, params);

  // prevent rendering any data if feature flag is not set for category
  if (
    (!CH_BUYING_FEATURE && category === BUYING_CATEGORY) ||
    (!CH_SELLING_FEATURE && category === SELLING_CATEGORY) ||
    (!CH_RENTING_FEATURE && category === RENTING_CATEGORY) ||
    (!CH_STUDENTS_FEATURE && category === STUDENTS_CATEGORY) ||
    (!NEWS_FEATURE && NEWS_CATEGORIES.includes(category))
  ) {
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
    };
  }

  const props = await chArticleSSR({
    article: article as string,
    articlesParams,
    category,
    defaultDescription: DEFAULT_META_DESCRIPTION,
    subCategories,
    subCategory: subCategory as string,
  });

  if (!props?.data)
    return {
      notFound: true,
    };

  return {
    props: {
      ...props,
      disableHeader: true,
      disableFooter: true,
    },
  };
};

const ContentHubSubArticle: React.FC<ContentHubArticleProps> = ({
  data,
  category,
  subCategory,
  slug,
  subCategoryName,
  categoryName,
  subCategories,
  relatedArticles,
  prevArticle,
  nextArticle,
  metaDescription,
}) => {
  return (
    <>
      <Head>
        {generateMetaTags({
          title: `${data?.attributes.title ?? 'Article'} - ${subCategoryName} - ${categoryName} - PropertyPal`,
          url: `/${category}/${subCategory}/${slug}`,
          description: metaDescription,
          image: data?.attributes.poster?.data?.attributes.url
            ? {
                height: data?.attributes.poster?.data?.attributes.height || 0,
                width: data?.attributes.poster?.data?.attributes.width || 0,
                url: data?.attributes.poster?.data?.attributes.url,
              }
            : undefined,
        })}
      </Head>

      <ContentHubLayout subCategories={subCategories}>
        <ContentHubPageContainer
          breadcrumbs={[
            CH_HOME_FEATURE ? { title: 'Home', url: CH_HOME_LINK } : undefined,
            { title: categoryName ?? category, url: `/${category}` },
            { title: subCategoryName ?? subCategory, url: `/${category}/${subCategory}` },
            { title: data?.attributes.title ?? slug },
          ]}
          poster={data?.attributes.poster?.data?.attributes.url}
          tabletPoster={data?.attributes.tabletPoster?.data?.attributes.url}
          mobilePoster={data?.attributes.mobilePoster?.data?.attributes.url}
          showSidebar={!!(data && data.attributes != null)}
          sidebarData={{
            tag: data?.attributes.tag?.data?.attributes,
            category,
            subCategory,
            prevArticle,
            nextArticle,
            articleTitle: data?.attributes.title ?? slug,
          }}
          articleHeroPosition={data?.attributes.articleHeroPosition}
        >
          <ArticleContent data={data} />
        </ContentHubPageContainer>
        <RelatedArticles relatedArticles={relatedArticles} />
      </ContentHubLayout>
    </>
  );
};

export default ContentHubSubArticle;
